// src/client/components/shared/DataTable.tsx
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@client/components/ui/table';
import { Button } from '@client/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@client/components/ui/tooltip';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@client/components/ui/alert-dialog';
import { Edit, Trash2, AlertTriangle } from 'lucide-react';

export interface TableColumn<T> {
  key: string;
  header: string;
  render: (item: T) => React.ReactNode;
  className?: string;
}

export interface TableAction<T> {
  icon: React.ReactNode;
  label: string;
  onClick: (item: T) => void;
  variant?: 'default' | 'destructive' | 'ghost';
  disabled?: (item: T) => boolean;
}

interface DataTableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  actions?: {
    onEdit?: (item: T) => void;
    onDelete?: (item: T) => void;
    onToggle?: (item: T) => void;
    custom?: TableAction<T>[];
  };
  emptyState?: {
    icon?: React.ReactNode;
    title: string;
    description?: string;
  };
  loading?: boolean;
  deleteConfirmation?: {
    title: string;
    description: (item: T) => string;
  };
  getRowKey: (item: T) => string | number;
}

export function DataTable<T>({
  data,
  columns,
  actions,
  emptyState,
  loading = false,
  deleteConfirmation,
  getRowKey,
}: DataTableProps<T>) {
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Empty state
  if (data.length === 0 && emptyState) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {emptyState.icon && (
          <div className="flex justify-center mb-4">
            {emptyState.icon}
          </div>
        )}
        <p className="font-medium">{emptyState.title}</p>
        {emptyState.description && (
          <p className="text-sm mt-1">{emptyState.description}</p>
        )}
      </div>
    );
  }

  const hasActions = actions && (actions.onEdit || actions.onDelete || actions.onToggle || actions.custom);

  return (
    <TooltipProvider>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.key} className={column.className}>
                  {column.header}
                </TableHead>
              ))}
              {hasActions && (
                <TableHead className="text-right">Aktionen</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item) => (
              <TableRow key={getRowKey(item)}>
                {columns.map((column) => (
                  <TableCell key={column.key} className={column.className}>
                    {column.render(item)}
                  </TableCell>
                ))}
                {hasActions && (
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* Edit Action */}
                      {actions.onEdit && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => actions.onEdit!(item)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Bearbeiten</p>
                          </TooltipContent>
                        </Tooltip>
                      )}

                      {/* Toggle Action */}
                      {actions.onToggle && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => actions.onToggle!(item)}
                            >
                              ACTION
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Status ändern</p>
                          </TooltipContent>
                        </Tooltip>
                      )}

                      {/* Custom Actions */}
                      {actions.custom?.map((action, idx) => (
                        <Tooltip key={idx}>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant={action.variant || 'ghost'}
                              onClick={() => action.onClick(item)}
                              disabled={action.disabled ? action.disabled(item) : false}
                            >
                              {action.icon}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{action.label}</p>
                          </TooltipContent>
                        </Tooltip>
                      ))}

                      {/* Delete Action with Confirmation */}
                      {actions.onDelete && (
                        <AlertDialog>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Löschen</p>
                            </TooltipContent>
                          </Tooltip>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="flex items-center gap-2">
                                <AlertTriangle className="h-5 w-5 text-red-600" />
                                {deleteConfirmation?.title || 'Eintrag löschen?'}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {deleteConfirmation?.description(item) || 
                                  'Sind Sie sicher, dass Sie diesen Eintrag löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.'}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => actions.onDelete!(item)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Löschen
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
}